import { Chart } from "chart.js";
import { v4 } from "uuid";
import trashIcon from '../../assets/trash-icon.svg';
import refreshIconImg from '../../assets/refresh-icon.svg';

class Charts {
    constructor(sheet) {
        this.sheet = sheet;
        this.clickHandler = this.handleClick.bind(this);
        this.dragStartHandler = this.dragStart.bind(this);
    }

    resetData(data, container) {
        this.clearCharts(container);
        this.drawCharts(data, container);
    }

    createChartContainer(chartId, container, message = null) {
        const chartContainer = document.createElement('div');
        chartContainer.className = 'chart';
        chartContainer.id = chartId;

        const sheetBounds = this.sheet.el.el.getBoundingClientRect();

        const defaultLeft = 300 + this.sheet.data.charts.length * 120;
        const defaultTop = 245 + this.sheet.data.charts.length * 80;

        let left = `${defaultLeft}px`;
        let top = `${defaultTop}px`;

        if ((defaultLeft + 500) >= sheetBounds.right) {
            left = `${sheetBounds.right - 500}px`;
        }
        if (defaultTop + 300 >= sheetBounds.bottom) {
            top = `${sheetBounds.bottom - 300}px`;
        }
        const chartHeaderContainer = this.createChartControlContainer(chartContainer);
        chartContainer.style.cssText = `
            width: 560px;
            position: fixed;
            height: 300px;
            top: ${top};
            left: ${left};
            z-index: 9999;
            background: white;
            border: 2px solid silver;
        `;
        chartContainer.appendChild(chartHeaderContainer);
        container.el.appendChild(chartContainer);

        if (message) {
            const messageElement = document.createElement('p');
            messageElement.textContent = message;
            messageElement.style.cssText = `
                position: relative;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 30px;
                color: #7a7777;
                top: 120px;
                left: 50px;
                width: 480px;
            `;
            chartContainer.appendChild(messageElement);
        }
        return chartContainer;
    }

    createContextMenu(e, chartContainer) {
        const existingDotsIcon = chartContainer.querySelector('.dots-icon');
        if (!existingDotsIcon) {
            const dotsIcon = document.createElement('div');
            dotsIcon.className = 'dots-icon';
            dotsIcon.innerHTML = '• • •';
            dotsIcon.style.cssText = `
                position: absolute;
                top: 10px;
                right: 10px;
                cursor: pointer;
                font-size: 18px;
                color:'#999';
                display: none;
            `;
            const contextMenu = document.createElement('div');
            contextMenu.className = 'context-menu';
            contextMenu.innerHTML = 'Delete';
            contextMenu.style.cssText = `
            position: absolute;
            top: 0px;
            left: 50px;
            background: white;
            border: 1px solid silver;
            cursor: pointer;
            font-size:12px;
            padding: 2px 8px;
            display:none;
        `;
            dotsIcon.appendChild(contextMenu);
            dotsIcon.addEventListener('click', () => {
                contextMenu.style.display = 'block';
            })
            chartContainer.appendChild(dotsIcon);
            contextMenu.addEventListener('click', (event) => this.handelContextMenu(event, chartContainer));
        }
        else {
            return;
        }
    }

    addChart(container, selectedData, chartType, dataRange) {
        // Generate unique chart ID
        const chartId = `chart-${v4()}`;
        const { labels, chartData, headerRow } = this.processChartData([...selectedData]);
        const xAxisHeader = headerRow?.[0]?.toString().trim() || 'Label';
        const yAxisHeader = headerRow?.[1]?.toString().trim() || 'Value';
        const validation = this.validateChartData(selectedData);
        const type = chartType === 'area' ? 'line' : chartType;
    
        const dataset = {
            label: `${yAxisHeader} vs. ${xAxisHeader}`,
            data: chartData,
            borderColor: 'rgba(54, 162, 235, 1)',
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
            borderWidth: 2,
            borderRadius: 5,
            borderSkipped: false,
            fill: chartType === 'area',
        };
    
        const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'top',
                    display: false,
                },
                title: {
                    display: true,
                    text: `${chartType} Chart`,
                },
            },
            scales: {
                xAxes: [{
                    scaleLabel: {
                        display: true,
                        labelString: xAxisHeader || 'Label',
                        padding: 10, // Adds space below the x-axis label
                    },
                    ticks: {
                        beginAtZero: true,
                        padding: 10, // Adds space between ticks and label
                    }
                }],
                yAxes: [{
                    scaleLabel: {
                        display: true,
                        labelString: yAxisHeader || 'Value',
                        padding: 10, // Adds space left of the y-axis label
                    },
                    ticks: {
                        beginAtZero: true,
                        min: 0,  
                        padding: 10, // Adds space between ticks and label
                    }
                }]
            },
        };
    
        const chartInfo = {
            id: chartId,
            chartType: type,
            dataRange,
            data: { labels, datasets: [dataset] },
            options,
        };
    
        let chartContainer;
    
        // Handle invalid chart case
        if (!validation.isValid) {
            chartContainer = this.createChartContainer(chartId, container, validation.message);
            this.storeChart({ ...chartInfo, isValidChart: false, message: validation.message }, chartContainer.style);
        } else {
            // Valid chart: create canvas and render
            chartContainer = this.createChartContainer(chartId, container);
            chartContainer.style.borderColor = 'red';
    
            const canvas = document.createElement('canvas');
            chartContainer.appendChild(canvas);
    
            const chart = new Chart(canvas, {
                type,
                data: {
                    labels,
                    datasets: [dataset],
                },
                options,
            });
    
            canvas.chartInstance = chart;
            this.storeChart({
                ...chartInfo,
                isValidChart: true,
                data: {
                    labels,
                    datasets: [{
                        label: `${yAxisHeader} vs. ${xAxisHeader}`,
                        data: chartData,
                        borderColor: 'rgba(54, 162, 235, 1)',
                        backgroundColor: 'rgba(54, 162, 235, 0.5)', borderWidth: 2, borderRadius: 5,
                        borderSkipped: false,
                        fill: chartType === 'area'
                    }]
                },
            }, chartContainer.style
            );
        }
        // Common event bindings
        document.addEventListener('click', event => this.clickHandler(chartContainer, event));
        // chartContainer.addEventListener('click', (event) => this.createContextMenu(event, chartContainer));
        chartContainer.addEventListener('mousedown', event => this.dragStartHandler(chartContainer, event));
    }
    clearCharts(container) {
        this.sheet.data.charts.forEach((chart) => {
            const element = document.getElementById(chart.id);
            this.removeEventListeners(element);
        });
        container.el.innerHTML = '';
    }
    drawCharts(data, container) {
        data.charts.forEach((storedItem, index) => {
            const chartContainer = this.createChartContainer(storedItem.id, container, storedItem.isValidChart === false ? storedItem.message : null);

            if (storedItem.isValidChart && storedItem.data) {
                const data = { labels: storedItem.data.labels, datasets: [{ ...storedItem.data.datasets[0] }] };
                const canvas = document.createElement('canvas');
                chartContainer.appendChild(canvas);
                const chart = new Chart(canvas, { type: storedItem.chartType, data, options: storedItem.options });
                canvas.chartInstance = chart;
            }
            if (storedItem.position) {
                chartContainer.style.top = storedItem.position.top;
                chartContainer.style.left = storedItem.position.left;
            }
            document.addEventListener('click', event => this.clickHandler(chartContainer, event));
            // chartContainer.addEventListener('click', (event) => this.createContextMenu(event, chartContainer));
            chartContainer.addEventListener('mousedown', event => this.dragStartHandler(chartContainer, event));
        });
    }

    storeChart(chartData, style) {
        this.sheet.data.charts.push({ ...chartData, position: { top: style.top, left: style.left } });
        let payload = this.sheet.data.getData();
        payload.charts = this.sheet.data.charts;
        this.sheet.trigger('change', payload);
    }

    handleClick(chartContainer, clickEvent) {
        const dotsIcon = chartContainer.querySelector('.dots-icon');
        if (!chartContainer.contains(clickEvent.target)) {
            chartContainer.style.borderColor = 'silver';
            if (dotsIcon) {
                chartContainer.removeChild(dotsIcon);
            }
            document.removeEventListener('click', this.clickHandler);
            chartContainer.removeEventListener('mousedown', this.dragStartHandler);
        }
        else {
            chartContainer.style.borderColor = '#4b89ff';
            if (dotsIcon) {
                dotsIcon.style.display = 'block';
            }
        }
    }
    dragStart(chartContainer, event) {
        const { sheet } = this;
        let offsetX = event.clientX - chartContainer.getBoundingClientRect().left;
        let offsetY = event.clientY - chartContainer.getBoundingClientRect().top;

        const handleMouseMove = (moveEvent) => {
            this.sheet.data.charts.forEach((chart) => {
                const element = document.getElementById(chart.id);
                const dotsIcon = element.querySelector('.dots-icon');
                if (element) {
                    element.style.borderColor = 'silver';
                    if (dotsIcon) {
                        element.removeChild(dotsIcon);
                    }
                }
            });
            chartContainer.style.borderColor = '#4b89ff';
            const x = moveEvent.clientX - offsetX;
            const y = moveEvent.clientY - offsetY;

            const sheetBounds = sheet.el.el.getBoundingClientRect();
            const chartRect = chartContainer.getBoundingClientRect();

            if (
                x > sheetBounds.left &&
                x + chartRect.width < sheetBounds.right &&
                y > sheetBounds.top &&
                y + chartRect.height < sheetBounds.bottom
            ) {
                chartContainer.style.left = `${x}px`;
                chartContainer.style.top = `${y}px`;
            } else {
                const { verticalScrollbar, horizontalScrollbar, data } = sheet;
                const { top } = verticalScrollbar.scroll();
                const { left } = horizontalScrollbar.scroll();

                const moveY = (vertical) => {
                    const { rows } = data;
                    const ri = data.scroll.ri - vertical;
                    if (ri >= 0 && ri < rows.len) {
                        const rh = rows.getHeight(ri);
                        verticalScrollbar.move({ top: top - vertical * rh });
                    }
                };
                const moveX = (horizontal) => {
                    const { cols } = data;
                    const ci = data.scroll.ci - horizontal;
                    if (ci >= 0 && ci < cols.len) {
                        const cw = cols.getWidth(ci);
                        horizontalScrollbar.move({ left: left - horizontal * cw });
                    }
                };
                if (x < sheetBounds.left) {
                    moveX(1); // Scroll to the right
                } else if (x + chartRect.width > sheetBounds.right) {
                    moveX(-1); // Scroll to the left
                }
                if (y < sheetBounds.top) {
                    moveY(1); // Scroll down
                } else if (y + chartRect.height > sheetBounds.bottom) {
                    moveY(-1); // Scroll up
                }
            }
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            const chartId = chartContainer.id;
            const storedChart = this.sheet.data.charts.find((chart) => chart.id === chartId);
            if (storedChart) {
                storedChart.position = { top: chartContainer.style.top, left: chartContainer.style.left };
                let payload = this.sheet.data.getData();
                payload.charts = this.sheet.data.charts;
                this.sheet.trigger('change', payload);
            }
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }

    removeEventListeners(element) {
        if (element) {
            document.removeEventListener('click', this.clickHandler);
            element.removeEventListener('mousedown', this.dragStartHandler);
        }
    }
    handelContextMenu(event, chartContainer) {
        event.preventDefault();
        const dotsIcon = chartContainer.querySelector('.dots-icon');
        chartContainer.removeChild(dotsIcon);
    }
    handelDeleteChart(event, chartContainer) {
        event.preventDefault();
        this.deleteChart(chartContainer.id);
    }

    handelRefreshChart(event, chartContainer) {
        event.preventDefault();
        const chartId = chartContainer.id;
        let charts = this.sheet.data.charts;

        const chartIndex = charts.findIndex(chart => chart.id === chartId);
        if (chartIndex === -1) return;

        const respectiveChart = charts[chartIndex];
        if (!respectiveChart) return;

        const range = respectiveChart.dataRange;
        const updatedData = this.sheet.getDataFromRange(
            range,
            this.sheet.data.getCell.bind(this.sheet.data)
        );

        const { labels, chartData, headerRow } = this.processChartData([...updatedData]);
        const xAxisHeader = headerRow[0]?.toString().trim() || 'Label';
        const yAxisHeader = headerRow[1]?.toString().trim() || 'Value';

        const validation = this.validateChartData(updatedData);
        if (!validation.isValid) {
            chartContainer.innerHTML = "";
            const messageElement = document.createElement('p');
            messageElement.textContent = validation.message;
            messageElement.style.cssText = `
                position: relative;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 30px;
                color: #7a7777;
                top: 120px;
                left: 50px;
            `;
            const chartHeaderContainer = this.createChartControlContainer(chartContainer);
            chartContainer.appendChild(chartHeaderContainer);
            chartContainer.appendChild(messageElement);
            let data = {
                ...respectiveChart,
                message: validation.message,
                isValidChart: false,
            };
            charts[chartIndex] = data;
            this.sheet.data.charts = [...charts];
            // Prepare updated payload and trigger change
            const payload = this.sheet.data.getData();
            payload.charts = this.sheet.data.charts
            this.sheet.trigger('change', payload);
            return;
        }
        const updatedChart = {
            ...respectiveChart,
            label: `${yAxisHeader} vs. ${xAxisHeader}`,
            isValidChart: true,
            data: {
                ...respectiveChart.data,
                labels,
                datasets: respectiveChart.data.datasets.map((dataset, index) => {
                    if (index === 0) {
                        return {
                            ...dataset,
                            data: chartData,
                            label: `${yAxisHeader} vs. ${xAxisHeader}`,
                        };
                    }
                    return dataset;
                }),
            },
            options: {
                ...respectiveChart.options,
                scales: {
                    xAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: xAxisHeader || 'Label', 
                        },
                        ticks: {
                            beginAtZero: true,
                        }
                    }],
                    yAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: yAxisHeader || 'Value', 
                        },
                        ticks: {
                            beginAtZero: true,
                        }
                    }]
                }
            }
        };
        charts[chartIndex] = updatedChart;
        this.sheet.data.charts = [...charts];
        this.updateChart(updatedChart, chartContainer);
    }

    updateChart(chartData, chartContainer) {
        let canvas = chartContainer.querySelector('canvas');
        if (!canvas) {
            chartContainer.innerHTML = "";
            const chartHeaderContainer = this.createChartControlContainer(chartContainer);
            chartContainer.appendChild(chartHeaderContainer);
            canvas = document.createElement('canvas');
            chartContainer.appendChild(canvas);
        }
        let chartInstance = canvas.chartInstance;

        if (chartInstance) {
            chartInstance.destroy();
        }
        // Create new chart from scratch
        chartInstance = new Chart(canvas, {
            type: chartData.chartType,
            data: JSON.parse(JSON.stringify(chartData.data)),
            options: chartData.options
        });
        canvas.chartInstance = chartInstance;
        // Prepare updated payload and trigger change
        const payload = this.sheet.data.getData();
        payload.charts = this.sheet.data.charts
        this.sheet.trigger('change', payload);
    }
    handleOutsideContextMenuClick(contextMenu, outsideEvent) {
        if (!contextMenu.contains(outsideEvent.target)) {
            document.body.removeChild(contextMenu);
            document.removeEventListener('click', this.handleOutsideContextMenuClick);
        }
    }
    deleteChart(chartId) {
        const { sheet } = this;
        const chartIndex = this.sheet.data.charts.findIndex((chart) => chart.id === chartId);
        if (chartIndex !== -1) {
            this.sheet.data.charts.splice(chartIndex, 1);
            let payload = this.sheet.data.getData();
            payload.charts = this.sheet.data.charts;
            this.sheet.trigger('change', payload);
        }
        const chartContainer = document.getElementById(chartId);
        if (chartContainer) {
            chartContainer.remove();

        }
    }

    createChartControlContainer(chartContainer) {
        const controlContainer = document.createElement('div');
        controlContainer.id = 'delete-chart';
        controlContainer.className = 'delete-chart-container';

        // Refresh icon
        const refreshIcon = document.createElement('img');
        refreshIcon.src = refreshIconImg;
        refreshIcon.style.width = '16px';
        refreshIcon.alt = 'Refresh Chart';
        refreshIcon.title = 'Refresh Chart';

        // Delete icon
        const deleteIcon = document.createElement('img');
        deleteIcon.src = trashIcon;
        deleteIcon.style.width = '14px';
        deleteIcon.alt = 'Delete Chart';
        deleteIcon.title = 'Delete Chart';

        deleteIcon.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handelDeleteChart(e, chartContainer);
        });
        refreshIcon.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handelRefreshChart(e, chartContainer);
        });

        // Append both icons
        controlContainer.appendChild(refreshIcon);
        controlContainer.appendChild(deleteIcon);

        return controlContainer;
    }
    processChartData(selectedData) {
        const headerRow = selectedData[0]
        const dataRows = selectedData.slice(1); // Skip the first row
        const labels = dataRows.map(row => row[0]?.toString().trim());

        const chartData = dataRows.map(row => {
            const val = row[1]?.toString().trim().replace(/\r/g, '');
            return (val === '' || isNaN(Number(val))) ? '' : Number(val);
        });

        const hasNumericData = chartData.some(val => typeof val === 'number' && !isNaN(val));

        return {
            labels,
            chartData,
            hasNumericData,
            headerRow
        };
    }
    validateChartData(selectedData) {
        const { labels, chartData, hasNumericData } = this.processChartData([...selectedData]);

        const allLabelsEmpty = labels.every(label => !label || label.trim() === '');
        const allDataEmptyOrInvalid = chartData.every(val => val === '' || typeof val !== 'number');

        const columnCount = Math.max(...selectedData.map(row => row.length));

        // Case 1: Only header or no data
        if (selectedData.length <= 1) {
            return {
                isValid: false,
                message: 'Select at least one row with both a label and a numeric value',
            };
        }

        // Case 2: Less than two columns
        if (columnCount < 2) {
            return {
                isValid: false,
                message: 'Select at least two columns: one for labels and one for numeric values',
            };
        }

        // Case 3: Both label and numeric data missing
        if (allLabelsEmpty && allDataEmptyOrInvalid) {
            return {
                isValid: false,
                message: 'Both labels and numeric values are required to create a chart',
            };
        }

        // Case 4: Labels exist, but no valid numeric data
        if (!allLabelsEmpty && allDataEmptyOrInvalid) {
            return {
                isValid: false,
                message: 'Your selection includes labels but no valid numeric data in the second column',
            };
        }

        // Case 5: Valid numeric values, but labels missing
        if (allLabelsEmpty && hasNumericData) {
            return {
                isValid: false,
                message: 'Your selection includes numeric values but no labels in the first column',
            };
        }

        return { isValid: true };
    }
}

export default Charts;
